import React, { useId, useState } from 'react';
import { Box, ButtonBase, Collapse } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

/**
 * A titled panel that opens and closes: a header in the card-header surface over a body in
 * the nested-panel surface, inside one hairline border. `headerEnd` sits at the right of the
 * header, after the title (a count, a status).
 *
 * `headerEnd` renders inside the header's button, so it must never be clickable: a control
 * nested in a button is invalid HTML, and its clicks would also toggle the panel.
 *
 * No `overflow: hidden` on the panel: it would clip the focus rings of controls inside.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the collapsible section.
 * @param {React.ReactNode} props.headerEnd - The content to render at the end of the header.
 * @param {boolean} [props.defaultOpen=false] - Whether the section should be open by default.
 * @param {React.ReactNode} props.children - The content to render inside the collapsible section.
 */
export default function CollapsibleSection({
  title,
  headerEnd,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const radius = (theme) => `${theme.shape.borderRadius}px`;

  return (
    <Box
      sx={{
        width: '100%',
        border: 1,
        borderColor: 'border',
        borderRadius: radius,
      }}
    >
      <ButtonBase
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen(!open)}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          gap: 1,
          px: 1.25,
          py: 1,
          bgcolor: 'cardHeaderBackground',
          color: 'text.primary',
          borderRadius: (theme) => (open
            ? `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`
            : radius(theme)),
          fontSize: '0.875rem',
          fontWeight: 600,
          // A layer over the header surface, as rows do, rather than a replacement color.
          '&:hover': {
            backgroundImage: (theme) => {
              const overlay = (theme.vars || theme).palette.rowHoverOverlay;
              return `linear-gradient(${overlay}, ${overlay})`;
            },
          },
        }}
      >
        <ChevronRight
          sx={{
            fontSize: '1.25rem',
            color: 'iconMuted',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 150ms ease-out',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>{title}</Box>
        {headerEnd}
      </ButtonBase>
      <Collapse in={open} id={bodyId}>
        <Box
          sx={{
            bgcolor: 'subtleBackground',
            borderTop: 1,
            borderColor: 'border',
            borderRadius: (theme) => `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
            px: 1.25,
            pt: 2.5,
            pb: 1.25,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
